import { ref } from 'vue';
import { route } from 'ziggy-js';
import { z } from 'zod';
import { Ok, Err, Result } from 'ts-results';

export const elementStyles = {
    base: {
        color: '#4b5563',
        fontWeight: 400,
        fontFamily:
            'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
        fontSize: '16px',
        fontSmoothing: 'antialiased',
        '::placeholder': {
            color: '#9fa6b2',
        },
        ':-webkit-autofill': {
            color: '#9fa6b2',
        },
        ':focus': {},
    },
    invalid: {
        color: '#e53e3e',
        '::placeholder': {
            color: '#9fa6b2',
        },
    },
};

export const rules = {
    '.Input': {
        padding: '20px 10px',
    },
};

export const elementClasses = {
    focus: 'focused',
    empty: 'empty',
    invalid: 'invalid',
};

export const billingDetailsSchema = z.object({
    name: z.string().min(2).max(128).trim(),
    country: z.string().min(2),
    postal_code: z.string().min(3).max(10),
});

export type BillingDetails = z.infer<typeof billingDetailsSchema>;

const DEV_KEY =
    'pk_test_51PQZGEP3oQ6EUAAiD96E6997UQQMdv2nBRYqH1jXUwKY91ZlQALkDl2v3Hn6Lcp7Dss4txQJ3WxtsShZJdBJApVo00qp9LyXnS';

const PROD_KEY =
    'pk_live_51PQZGEP3oQ6EUAAiFGY6YpskixngGc9hrbYAfreurMoj3QrFS6l3dCovrf5LvF204J2ABIjTrWPvC8RR9SrGiCFW00gW1zFjBe';

const STRIPE_KEY = import.meta.env.DEV ? DEV_KEY : PROD_KEY;

export function useStripeClient(billingAddress: 'pro' | 'plugin') {
    const stripe = ref<stripe.Stripe>();
    const stripeElements = ref<stripe.elements.Elements>();
    const stripeCardNumber = ref<stripe.elements.Element>();
    const stripeCardExpiry = ref<stripe.elements.Element>();
    const stripeCardCvc = ref<stripe.elements.Element>();
    const clientSecret = ref<string | null>(null);
    const subscriptionType = ref<'pro' | 'plugin'>(billingAddress);

    async function init() {
        if (stripe.value !== undefined) {
            return;
        }

        clientSecret.value = await getStripeIntent(billingAddress);

        stripe.value = Stripe(STRIPE_KEY);
    }

    async function confirmStripeCardPayment(secret: string) {
        await init();

        const result = await stripe.value.confirmCardPayment(secret);

        return result.error ? false : true;
    }

    async function createHostedFields() {
        await init();

        if (stripe.value === undefined) {
            return new Err(new Error('Stripe not initialized'));
        }
        stripeElements.value = stripe.value.elements();
        stripeCardNumber.value = stripeElements.value.create('cardNumber', {
            style: elementStyles,
            classes: elementClasses,
        });
        stripeCardExpiry.value = stripeElements.value.create('cardExpiry', {
            style: elementStyles,
            classes: elementClasses,
        });
        stripeCardCvc.value = stripeElements.value.create('cardCvc', {
            style: elementStyles,
            classes: elementClasses,
        });

        stripeCardNumber.value.mount('#cardNumber');
        stripeCardExpiry.value.mount('#cardExpirationDate');
        stripeCardCvc.value.mount('#cardCvv');
    }

    async function getStripeIntent(subscriptionType: string) {
        try {
            const response = await axios.get(route('stripe.setup-intent'), {
                params: {
                    type: subscriptionType,
                },
            });

            if (response.data.success) {
                return response.data.data.clientSecret;
            }
            return null;
        } catch (e) {
            console.error('Error fetching Stripe Setup Intent:', e);
            return null;
        }
    }

    async function confirmCardSetupAndSave(
        billingDetails: BillingDetails
    ): Promise<Result<string, Error>> {
        if (stripe.value === undefined) {
            return new Err(new Error('Stripe not initialized'));
        }

        if (stripeCardNumber.value === undefined) {
            return new Err(new Error('Stripe card number not initialized'));
        }

        try {
            const response = await stripe.value.confirmCardSetup(
                clientSecret.value,
                {
                    payment_method: {
                        card: stripeCardNumber.value,
                        billing_details: {
                            name: billingDetails.name,
                            address: {
                                country: billingDetails.country,
                                postal_code: billingDetails.postal_code,
                            },
                        },
                    },
                }
            );

            if (response.error) {
                // Inform the user if there was an error.
                const errorElement = document.getElementById('card-errors');

                if (errorElement) {
                    errorElement.textContent = String(response.error.message);
                }

                return new Err(new Error(String(response.error)));
            }

            // Adding a card should not silently make it the default — the user can flip
            // the default explicitly via the "Make default" action. We still need a default
            // set somewhere though, so do it only when this is the customer's *first* card.
            try {
                const existing = await getPaymentMethods();
                const hasDefault = (existing?.data?.data ?? []).some(
                    (m: { default?: boolean }) => m.default,
                );

                if (!hasDefault) {
                    const setDefaultPaymentMethodResponse =
                        await setDefaultPaymentMethod(
                            response.setupIntent.payment_method,
                        );

                    if (setDefaultPaymentMethodResponse.status !== 200) {
                        return new Err(
                            new Error('Failed to set default payment method'),
                        );
                    }
                }
            } catch (e) {
                console.error('[useStripe] post-add default check failed:', e);
            }

            return new Ok(String(response.setupIntent.payment_method));
        } catch (err) {
            return new Err(new Error(String(err)));
        }
    }

    async function addPaymentMethod(billingDetails: BillingDetails): Promise<string | null> {
        try {
            await init();

            if (stripe.value === undefined) {
                return null;
            }

            if (stripeCardNumber.value === undefined) {
                return null;
            }

            const result = await confirmCardSetupAndSave(billingDetails);

            if (result.err) {
                throw new Error('Error confirming card setup');
            }

            create({
                title: 'Payment method added',
                text: 'Your new payment method has been successfully added.',
                type: 'success',
            });

            return result.val;
        } catch (error) {
            create({
                title: 'Adding payment method failed',
                text:
                    error.message ||
                    'An error occurred while adding the payment method.',
                type: 'error',
            });
            return null;
        }
    }

    const getPaymentMethods = async () => {
        return axios.get(
            route('stripe.payment-methods.index', {
                type: subscriptionType.value,
            })
        );
    };

    const setDefaultPaymentMethod = async (token: string) => {
        return axios.post(route('stripe.payment-methods.make-default'), {
            token: token,
            type: subscriptionType.value,
        });
    };

    const deletePaymentMethod = async (token: string) => {
        return axios.post(route('stripe.payment-methods.delete'), {
            token: token,
            type: subscriptionType.value,
        });
    };

    return {
        createHostedFields,
        addPaymentMethod,
        getPaymentMethods,
        setDefaultPaymentMethod,
        deletePaymentMethod,
        confirmStripeCardPayment,
    };
}
